import { useTheme } from '@emotion/react';
import PropTypes from 'prop-types';
import { forwardRef, useState } from 'react';

import Avatar from 'shopper/components/Avatar';
import Box from 'shopper/components/Box';
import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Icon from 'shopper/components/Icon';
import Text from 'shopper/components/Text';
import Tooltip from 'shopper/components/Tooltip';

import Anchor from 'components/Anchor';
import TextWithEllipsis from 'components/TextWithEllipsis';

import { toStoreOffersLink } from 'lib/links';

import StoreInfoChecks from './StoreInfoChecks';

import HEADING from 'constants/heading';

const MARKETPLACE_DISCLAIMER =
  'Marketplace é uma plataforma na qual lojistas e vendedores anunciam seus produtos dentro do site de outra loja. No Promobit, além da segurança dos sites, também verificamos a confiabilidade dos vendedores de marketplace.';

const StoreInfo = forwardRef(
  (
    {
      storeDomain,
      storeName,
      storeParam,
      storeDescription,
      storeImage,
      isStoreVerified,
      isStoreCnpjValid,
      hasPhysicalStore,
      storeMarketplace,
    },
    ref
  ) => {
    const theme = useTheme();
    const [isTooltipOpen, setTooltipOpen] = useState(false);

    return (
      <Box ref={ref} mb={4}>
        <Flex
          alignItems="flex-start"
          flexDirection="column"
          py={4}
          px={[4, null, 6]}
        >
          <Heading size="size5" as="span" mb={2}>
            {HEADING.STORE_INFO}
          </Heading>
          <Flex flexWrap="wrap" flexDirection="column" pt={4} full>
            {storeName ? (
              <>
                <Anchor
                  display="flex"
                  borderBottom="1px solid"
                  pb={4}
                  mb={4}
                  borderColor={theme.app.storeInfo.border}
                  href={toStoreOffersLink(storeParam)}
                  width={[null, null, '50%', null, '100%']}
                >
                  <Avatar
                    size="size3"
                    loading="lazy"
                    src={storeImage}
                    description={storeName}
                  />
                  <Flex
                    flexDirection="column"
                    justifyContent="flex-start"
                    overflow="hidden"
                    ml={4}
                  >
                    <Flex alignItems="center">
                      <Heading size="size6" as="span" bold>
                        {storeName}
                      </Heading>
                      <Icon name="verified" size="size4" ml={[1, null, 2]} />
                    </Flex>
                    <TextWithEllipsis>{storeDomain}</TextWithEllipsis>
                  </Flex>
                </Anchor>
                <Text size="size2" mb={4}>
                  {storeDescription}
                </Text>
              </>
            ) : (
              <Text mb={4}>{storeDomain}</Text>
            )}
            {storeMarketplace && (
              <Flex
                borderTop="1px solid"
                flexDirection="column"
                borderColor={theme.app.storeInfo.border}
                pt={4}
                mb={4}
              >
                <Flex alignItems="center" position="relative">
                  <Text bold>Sobre o Marketplace</Text>
                  <Icon
                    name="help-sign"
                    aria-describedby="marketplace-disclamer"
                    size="size3"
                    ml={1}
                    color={theme.app.storeInfo.helpSignIcon}
                    onMouseOver={() => setTooltipOpen(true)}
                    onMouseLeave={() => setTooltipOpen(false)}
                  />
                  <Tooltip
                    id="marketplace-disclamer"
                    left={['60%', null, '30%']}
                    width="14rem"
                    isOpen={isTooltipOpen}
                  >
                    {MARKETPLACE_DISCLAIMER}
                  </Tooltip>
                </Flex>
                <Text
                  color={theme.app.storeInfo.storeMarketplaceForeground}
                  mt={4}
                  bold
                >
                  {storeMarketplace.storeMarketplaceName}
                </Text>
              </Flex>
            )}
            <StoreInfoChecks
              isMarketplace={!!storeMarketplace}
              isVerified={
                storeMarketplace
                  ? storeMarketplace.isMarketplaceVerified
                  : isStoreVerified
              }
              isCnpjValid={
                storeMarketplace
                  ? storeMarketplace.isMarketplaceCnpjValid
                  : isStoreCnpjValid
              }
              hasPhysicalStore={
                storeMarketplace
                  ? storeMarketplace.hasPhysicalStore
                  : hasPhysicalStore
              }
            />
          </Flex>
        </Flex>
      </Box>
    );
  }
);

StoreInfo.displayName = 'StoreInfo';

StoreInfo.defaultProps = {
  storeDomain: null,
  storeName: null,
  storeParam: null,
  storeDescription: null,
  storeImage: null,
  storeMarketplace: null,
  isStoreVerified: false,
  isStoreCnpjValid: false,
  hasPhysicalStore: false,
};

StoreInfo.propTypes = {
  storeDomain: PropTypes.string,
  storeName: PropTypes.string,
  storeParam: PropTypes.string,
  storeDescription: PropTypes.string,
  storeImage: PropTypes.string,
  storeMarketplace: PropTypes.shape({
    isMarketplaceVerified: PropTypes.bool.isRequired,
    isMarketplaceCnpjValid: PropTypes.bool.isRequired,
    hasPhysicalStore: PropTypes.bool.isRequired,
  }),
  isStoreVerified: PropTypes.bool,
  isStoreCnpjValid: PropTypes.bool,
  hasPhysicalStore: PropTypes.bool,
};

export default StoreInfo;
